// src/brand.jsx
// Extracted from App.jsx — behavior-preserving split (2026-07-20).

import React from "react";
import { BRAND, C, PUBLIC_DISCLOSURES } from './theme';

// ═══════ BRAND MARKS — ported from the Reverse by Reece letter brand system ═══════
// REVERSE by REECE wordmark. tone: navy (light bg) | cream (dark bg) | clay.
const Wordmark=({tone="navy",size=18,sub,showPoweredBy=true})=>{
  const color=tone==="cream"?C.cream:tone==="clay"?C.clay:C.navy;
  const accent=tone==="cream"?C.blue:C.clay;
  const subline=sub!=null?sub:(showPoweredBy?"Powered by Summit Lending":null);
  return(
    <div style={{display:"inline-flex",flexDirection:"column",gap:2,lineHeight:1}}>
      <div className="wordmark" style={{fontSize:size,color}}>
        <span className="wm-r">REVERSE</span>
        <span className="wm-by" style={{color:accent}}>by</span>
        <span className="wm-reece">REECE</span>
      </div>
      {subline&&<div style={{fontSize:size*0.36,letterSpacing:"0.22em",fontWeight:600,color,opacity:0.7,textTransform:"uppercase",marginTop:size*0.18}}>{subline}</div>}
    </div>
  );
};

// Tiny Summit house mark (drawn, brand-aligned)
const SummitMark=({size=22,color="currentColor"})=>(
  <svg width={size} height={size*0.85} viewBox="0 0 32 28" fill="none" aria-hidden="true">
    <path d="M4 14 L16 4 L28 14" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M8 14 V24 H24 V14" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="16" y1="4" x2="16" y2="2" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const SummitLockup=({tone="navy",size=11})=>{
  const color=tone==="cream"?C.cream:C.navy;
  return(
    <div style={{display:"inline-flex",alignItems:"center",gap:7,color}}>
      <SummitMark size={size*1.8} color={color}/>
      <div style={{display:"flex",flexDirection:"column",lineHeight:1,gap:1}}>
        <span style={{fontWeight:800,fontSize:size,letterSpacing:"0.02em"}}>Summit</span>
        <span style={{fontWeight:600,fontSize:size*0.6,letterSpacing:"0.22em",opacity:0.75}}>LENDING</span>
      </div>
    </div>
  );
};

// Equal Housing Opportunity — minimal monochrome
const EHOMark=({color="currentColor",size=14})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="22" height="22" rx="1.5" stroke={color} strokeWidth="1.4" fill="none"/>
    <path d="M5 14 L12 8 L19 14" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M7 14 V18 H17 V14" stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
    <line x1="6.5" y1="11" x2="17.5" y2="11" stroke={color} strokeWidth="1.4"/>
  </svg>
);

// Compliance bar — Equal Housing + NMLS + licensed states. tone navy|cream.
const ComplianceBar=({tone="navy",size=8.5,condensed=false})=>{
  const color=tone==="cream"?C.cream:C.navy;
  const muted=tone==="cream"?"rgba(242,239,231,0.7)":"rgba(11,42,74,0.65)";
  return(
    <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:condensed?10:12,color,fontSize:size,fontWeight:600,letterSpacing:"0.06em",lineHeight:1.4}}>
      <EHOMark color={color} size={size*1.8}/>
      <span style={{fontWeight:700,letterSpacing:"0.14em"}}>EQUAL HOUSING&nbsp;OPPORTUNITY</span>
      <span style={{color:muted}}>·</span>
      <span style={{color:muted}}>{BRAND.loNmls}</span>
      <span style={{color:muted}}>·</span>
      <span style={{color:muted}}>{BRAND.companyNmls}</span>
      {!condensed&&(<>
        <span style={{color:muted}}>·</span>
        <span style={{color:muted}}>{BRAND.azLicense}</span>
        <span style={{color:muted}}>·</span>
        <span style={{color:muted,letterSpacing:"0.10em"}}>LICENSED IN&nbsp;{BRAND.licensedStates}</span>
      </>)}
    </div>
  );
};

// Centered brand lockup for login / public / borrower headers.
// Replaces the old Summit house logo with the primary REVERSE by REECE wordmark.
// `size` keeps backward-compat with prior call sites (scales the wordmark).
const SummitLogo=({size=1,dark=false})=>(
  <div style={{display:"inline-flex",flexDirection:"column",alignItems:"center",gap:size*8}}>
    <Wordmark tone={dark?"cream":"navy"} size={Math.round(28*size)}/>
  </div>
);

// Compact inline brand mark for the admin nav sidebar (navy-deep bg → cream tone).
const BrandMark=({dark=true})=>{
  const tone=dark?"cream":"navy";
  const dim=dark?"rgba(242,239,231,0.55)":C.textDim;
  return(
    <div>
      <Wordmark tone={tone} size={16} showPoweredBy={false}/>
      <div style={{marginTop:9,opacity:0.92}}><SummitLockup tone={tone} size={9}/></div>
      <div style={{fontSize:8,color:dim,marginTop:8,letterSpacing:"0.04em"}}>{BRAND.loNmls} · {BRAND.companyNmls}</div>
      <div style={{fontSize:8,color:dim,marginTop:2,opacity:.85}}>{BRAND.azLicense}</div>
    </div>
  );
};


// ═══════ PUBLIC FOOTER — the one compliance footer for public pages ═══════
// Renders the entire shared block from PUBLIC_DISCLOSURES (theme.js): EHO mark,
// NMLS attribution, licensed states, HUD/FHA + obligations + non-FHA-age
// disclosures, and the state-specific advertising disclosures. Used by the
// direct-mail landing page and /info so the two footers cannot drift.
const PublicFooter=()=>{
  const D=PUBLIC_DISCLOSURES;
  const dim="rgba(11,42,74,0.62)";
  const p={fontSize:12,color:dim,lineHeight:1.65,margin:"8px auto 0",maxWidth:640,fontWeight:500};
  return(
    <div style={{textAlign:"center",fontFamily:"'Mulish',system-ui,sans-serif"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,flexWrap:"wrap",color:C.navy,fontSize:11,fontWeight:700,letterSpacing:"0.08em"}}>
        <EHOMark color={C.navy} size={16}/>
        <span style={{textTransform:"uppercase",letterSpacing:"0.12em"}}>Equal Housing Opportunity</span>
      </div>
      <div style={{fontSize:12,fontWeight:700,color:C.navy,marginTop:10,letterSpacing:"0.02em"}}>{D.attribution}</div>
      <div style={{fontSize:11.5,color:dim,marginTop:4,fontWeight:600}}>Licensed in: {BRAND.licensedStates}</div>
      <p style={p}>{D.hud}</p>
      <p style={p}>{D.nonFhaAge}</p>
      <p style={p}>{D.obligations}</p>
      {D.stateDisclosures.map((s,i)=><p key={i} style={p}>{s}</p>)}
      <div style={{fontSize:11,color:dim,marginTop:14,fontWeight:600,letterSpacing:"0.04em"}}>© {new Date().getFullYear()} Reverse by Reece, powered by Summit Lending</div>
    </div>
  );
};

// Single shared font loader — prevents duplicate <link> injections across views
const FontLoader=()=><link href="https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,500;1,600&display=swap" rel="stylesheet"/>;

export { Wordmark, SummitMark, SummitLockup, EHOMark, ComplianceBar, SummitLogo, BrandMark, FontLoader, PublicFooter };
